import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import { RemoveScroll } from "react-remove-scroll";
import useWindowSize from "../../hooks/useWindowSize";
import { matchOverlayRoute } from "../Routes";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import Icon from "../Icon";
import "./style.scss";

const GenericFlyout = ({
  id,
  isOpen = false,
  close /* Remember to memoize this function */,
  keepMounted = false,
  fillHeight = false,
  minHeight = 6 /* rem */,
  width = 32 /* rem */,
  noCloseBtn = false,
  align = "top",
  isMobileSubMenu = false,
  ariaLabel = "",
  children
}: {
  id?: string;
  isOpen?: boolean;
  close: () => void;
  keepMounted?: boolean;
  fillHeight?: boolean;
  minHeight?: number;
  width?: number;
  noCloseBtn?: boolean;
  align?: "top" | "left" | "right" | "top-left" | "top-center";
  offset?: number;
  isMobileSubMenu?: boolean;
  ariaLabel?: string;
  children: ReactNode;
}) => {
  const t = useBjelinTranslations();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const isTouchDevice = useMediaQuery({ query: "(pointer: coarse)" });
  const winDim = useWindowSize();
  const { registerFlyoutOpened, registerFlyoutClosed } = useOverlayContext();

  const location = useLocation();
  const _isOpen = useRef(isOpen);
  const isFirst = useRef(true);
  const [below, setBelow] = useState(false);
  const _isBelow = useRef(false);
  const prevLocation = useRef({ ...location });
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const portalId = "flyout-portal-root";
  const contentRef = useRef<HTMLDivElement>(null);

  const baseFontSize: number = parseInt(
    window
      .getComputedStyle(document.documentElement)
      .getPropertyValue("font-size")
      .match(/\d+/)?.[0] || "16"
  );

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
    } else {
      const isOverlayRoute = matchOverlayRoute(location) !== undefined;
      if (isOverlayRoute) {
        _isBelow.current = _isOpen.current;
        setBelow(_isOpen.current);
      } else {
        if (
          location.pathname !== prevLocation.current.pathname &&
          _isOpen.current &&
          !_isBelow.current
        ) {
          // Close on location change while open
          //console.log("location change close");
          prevLocation.current = { ...location };
          close?.();
        }
      }
    }
  }, [location, close]);

  useEffect(() => {
    setTimeout(() => {
      _isOpen.current = isOpen;
    }, 100);
    if (isOpen) {
      _isBelow.current = false;
      setBelow(false);
      registerFlyoutOpened();
      setTimeout(() => {
        if (
          !isTouchDevice &&
          contentRef.current?.getElementsByTagName("input").item(0)
        )
          contentRef.current?.getElementsByTagName("input").item(0)?.select();
        else if (contentRef.current?.getElementsByTagName("button").item(0))
          contentRef.current?.getElementsByTagName("button").item(0)?.focus();
        else if (contentRef.current?.getElementsByTagName("a").item(0))
          contentRef.current?.getElementsByTagName("a").item(0)?.focus();
      }, 500);
    } else {
      registerFlyoutClosed();
    }
  }, [isOpen, isTouchDevice, registerFlyoutClosed, registerFlyoutOpened]);

  useLayoutEffect(() => {
    let _modalRoot = document.getElementById(portalId);
    if (!_modalRoot) {
      _modalRoot = document.createElement("div");
      _modalRoot.setAttribute("id", portalId);
      document.body.append(_modalRoot);
    }
    setModalRoot(_modalRoot);
  }, []);

  if (!modalRoot) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={"flyout-cover-" + id}
          id="flyout-cover"
          aria-hidden
          style={{
            zIndex: below ? 0 : 1201,
            top: "calc(var(--padding-top)"
          }}
          initial={{ opacity: 0, pointerEvents: "none" }}
          animate={{ opacity: 1, pointerEvents: "all" }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          onClick={e => {
            if ((e.target as HTMLElement).id === "flyout-cover") {
              close?.();
            }
            e.stopPropagation();
          }}
        ></motion.div>
      )}
      {(isOpen || keepMounted) && (
        <motion.div
          ref={contentRef}
          key={"flyout-content-" + id}
          id={id ? id : "flyout-content"}
          role="dialog"
          aria-label={ariaLabel}
          className={"flyout-content"}
          initial={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          animate={{
            opacity: !isOpen && keepMounted ? 0 : 1,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: !isOpen && keepMounted ? "-100%" : 0 }
              : { y: !isOpen && keepMounted ? "-100%" : 0 }),
            pointerEvents: !isOpen && keepMounted ? "none" : "all"
          }}
          exit={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          style={{
            zIndex: below ? 11 : 1202,
            ...(isSmall
              ? {}
              : {
                  top: `calc(var(--padding-top) + var(--menu-height))`,
                  minHeight: minHeight + "rem",
                  maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--menu-height) - var(--padding-top))`,
                  ...(fillHeight && {
                    bottom: 0
                  }),
                  ...(align === "left"
                    ? { left: 0, right: "auto" }
                    : align === "top-left"
                    ? {
                        left:
                          winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        right: "auto",
                        width: width + "rem",
                        maxWidth: "100vw"
                      }
                    : {
                        left: "auto",
                        right:
                          align === "top-center"
                            ? winDim.innerWidth / baseFontSize > width
                              ? (winDim.innerWidth / baseFontSize - width) / 2 +
                                "rem"
                              : 0
                            : winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        width: width + "rem",
                        maxWidth: "100vw"
                      })
                })
          }}
        >
          {(!noCloseBtn || (isMobileSubMenu && isSmall)) && (
            <button
              style={{
                float: "none",
                position: "absolute",
                zIndex: 10,
                top: "1rem",
                lineHeight: "2rem",
                border: "none",
                background: "#fff",
                borderRadius: "1em",
                ...(isMobileSubMenu
                  ? {
                      left: "1.5rem",
                      display: "flex",
                      columnGap: "0.3rem"
                    }
                  : {
                      right: "1.5rem"
                    })
              }}
              onClick={() => {
                close?.();
              }}
            >
              {isMobileSubMenu ? (
                <>
                  <Icon name="chevron-left" type="fal" />{" "}
                  <span
                    style={{
                      display: "inline-block",
                      height: "2rem",
                      lineHeight: "2rem",
                      fontSize: "1rem",
                      fontWeight: 400,
                      verticalAlign: "top"
                    }}
                  >
                    {t("back")}
                  </span>
                </>
              ) : (
                <Icon name="xmark" type="fal" />
              )}
            </button>
          )}
          <ErrorBoundary
            boundaryId={"flyout-" + (id ? id : "unspecified")}
            fallback={<ErrorDisplaySmall />}
          >
            <RemoveScroll
              removeScrollBar={false}
              enabled={!below && !(keepMounted && !isOpen)}
            >
              {children}
            </RemoveScroll>
          </ErrorBoundary>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, _e) => {
              switch (key) {
                case "esc":
                  close?.();
                  break;
                default:
              }
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    modalRoot
  );
};

export default GenericFlyout;
